import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, Inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ACLService } from '@delon/acl';
import { ALLOW_ANONYMOUS, DA_SERVICE_TOKEN, ITokenModel, ITokenService } from '@delon/auth';
import { ALAIN_I18N_TOKEN, MenuService, SettingsService, TitleService, _HttpClient } from '@delon/theme';
import type { NzSafeAny } from 'ng-zorro-antd/core/types';
import { NzIconService } from 'ng-zorro-antd/icon';
import { Observable, zip, of, catchError, map, filter, switchMap } from 'rxjs';
import { IJsonTable } from '@shared/model';

import { I18NService } from '../i18n/i18n.service';
import { ICONS } from '../../../style-icons';
import { ICONS_AUTO } from '../../../style-icons-auto';
import { environment } from '@env/environment';
import { ApplicationLogic } from '../logic/application.logic';
import { PrivilegeService } from '../service/privilege.service';
import { jerror } from '@shared';

/**
 * Used for application startup
 * Generally used to get the basic data of the application, like: Menu Data, User Data, etc.
 */
@Injectable()
export class StartupService {
  constructor(
    iconSrv: NzIconService,
    @Inject(ALAIN_I18N_TOKEN) private i18n: I18NService,
    private titleService: TitleService,
    @Inject(DA_SERVICE_TOKEN) private tokenSrv: ITokenService,
    private httpClient: HttpClient,
    private injector: Injector,
    private http: _HttpClient,
    private privilegeSrv: PrivilegeService
  ) {
    iconSrv.addIcon(...ICONS_AUTO, ...ICONS);
  }
  /**
   * 是否刷新token中
   */
  private refreshToking = false;

  /**
   *
   *
   * @author 王阮强(wangruanqiang@youzhibo.cn)
   * @date 2021-02-04
   * @private
   */
  private init() {
    // 订阅token失效事件
    this.subscribeRefresh();
  }

  /**
   * 刷新token
   *
   * @author 王阮强(wangruanqiang@youzhibo.cn)
   * @date 2021-02-02
   * @protected
   * @returns {*}  {Observable<IJsonTable>}
   */
  protected refreshToken(tokenData: ITokenModel): Observable<IJsonTable> {
    // const tokenData = (this.tokenSrv.get() || {}) as ITokenModel,
    const refreshToken = tokenData['refresh_token'];
    return this.http.put<IJsonTable>(`${this.privilegeSrv.appTypeName}/v1/session/${tokenData.token}`, {
      refresh_token: refreshToken
    });
  }
  /**
   * 订阅token失效信息
   *
   * @author 王阮强(wangruanqiang@youzhibo.cn)
   * @date 2021-02-02
   * @protected
   */
  protected subscribeRefresh(): void {
    this.tokenSrv.refresh
      .pipe(
        filter(() => !this.refreshToking),
        switchMap(tokenData => {
          if (tokenData['refresh_expired'] < +new Date()) {
            // 本地刷新令牌有效期已过期，则直接返回错误信息跳转登录页面
            return of(jerror('刷新令牌无效', 12));
          }
          this.refreshToking = true;
          return this.refreshToken(tokenData);
        })
      )
      .subscribe((result: IJsonTable) => {
        this.refreshToking = false;
        // 刷新失败
        if (0 != result.state) {
          return ApplicationLogic.getInstance(this.injector).toLogin(`${result.msg}[${result.state}]`);
        }
        // 刷新成功，设置token，合并本地原有token信息
        ApplicationLogic.getInstance(this.injector).load({
          ...this.tokenSrv.get(),
          ...result.data,
          expired: +new Date() + (result.data.expire_in - 30 * 60) * 1000,
          refresh_expired: +new Date() + (result.data.expire_in - 5 * 60) * 1000
        });
      });
  }
  changeFavicon() {
    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.type = 'image/x-icon';
    newLink.href = environment['img'].favicon; // 替换为新的 favicon 文件路径
    const existingLink = document.querySelector('link[rel="icon"]');
    if (existingLink) {
      document.head.removeChild(existingLink);
    }
    document.head.appendChild(newLink);
  }
  private viaHttp(): Observable<void> {
    const defaultLang = this.i18n.defaultLang;
    return zip(
      this.i18n.loadLangData(defaultLang),
      this.httpClient.get('assets/tmp/app-data.json', { context: new HttpContext().set(ALLOW_ANONYMOUS, true) })
    ).pipe(
      catchError((res: NzSafeAny) => {
        console.warn(`StartupService.load: Network request failed`, res);
        // setTimeout(() => this.router.navigateByUrl(`/exception/500`));
        return [];
      }),
      map(([langData, appData]: [Record<string, string>, NzSafeAny]) => {
        // setting language data
        this.i18n.use(defaultLang, langData);
      })
    );
  }

  load(): Observable<void> {
    // 未来可以删除该代码
    /** @deprecated */
    if (window.location.hash.indexOf('/transfer/transfer?token=') == -1) {
      this.init();
    }
    return this.viaHttp();
  }
}
